import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerRefreshVault: build.mutation<
      ApiControllerRefreshVaultApiResponse,
      ApiControllerRefreshVaultApiArg
    >({
      query: (queryArg) => ({
        url: `/web/refresh_vault/${queryArg.vaultId}`,
        method: 'POST',
      }),
    }),
    apiControllerConvert: build.mutation<
      ApiControllerConvertApiResponse,
      ApiControllerConvertApiArg
    >({
      query: (queryArg) => ({
        url: `/web/convert`,
        method: 'POST',
        body: queryArg.convertRequest,
      }),
    }),
    apiControllerWithdraw: build.mutation<
      ApiControllerWithdrawApiResponse,
      ApiControllerWithdrawApiArg
    >({
      query: (queryArg) => ({
        url: `/web/withdraw`,
        method: 'POST',
        body: queryArg.withdrawRequest,
      }),
    }),
    apiControllerGetExecutors: build.query<
      ApiControllerGetExecutorsApiResponse,
      ApiControllerGetExecutorsApiArg
    >({
      query: (queryArg) => ({ url: `/web/executors/${queryArg.vaultId}` }),
    }),
    apiControllerGetVaultStats: build.query<
      ApiControllerGetVaultStatsApiResponse,
      ApiControllerGetVaultStatsApiArg
    >({
      query: (queryArg) => ({
        url: `/web/stats/${queryArg.vaultId}/${queryArg['from']}/${queryArg.till}`,
      }),
    }),
    apiControllerGetVaultBalance: build.query<
      ApiControllerGetVaultBalanceApiResponse,
      ApiControllerGetVaultBalanceApiArg
    >({
      query: (queryArg) => ({ url: `/web/balance/${queryArg.vaultId}` }),
    }),
    apiControllerConvertToRaw: build.query<
      ApiControllerConvertToRawApiResponse,
      ApiControllerConvertToRawApiArg
    >({
      query: (queryArg) => ({
        url: `/web/toraw/${queryArg.amount}/${queryArg.decimals}`,
      }),
    }),
    apiControllerGetVaults: build.query<
      ApiControllerGetVaultsApiResponse,
      ApiControllerGetVaultsApiArg
    >({
      query: (queryArg) => ({
        url: `/web/vaults/${queryArg.projectId}/${queryArg.all}/${queryArg.mine}/${queryArg.limit}/${queryArg.page}`,
        params: {
          search: queryArg.search,
        },
      }),
    }),
    apiControllerGetVault: build.query<
      ApiControllerGetVaultApiResponse,
      ApiControllerGetVaultApiArg
    >({
      query: (queryArg) => ({ url: `/web/vault/${queryArg.vaultId}` }),
    }),
    apiControllerGetVaultSource: build.query<
      ApiControllerGetVaultSourceApiResponse,
      ApiControllerGetVaultSourceApiArg
    >({
      query: (queryArg) => ({ url: `/web/vault/${queryArg.vaultId}/source` }),
    }),
    apiControllerUpdateVaultStatus: build.mutation<
      ApiControllerUpdateVaultStatusApiResponse,
      ApiControllerUpdateVaultStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/web/update_status_vault/${queryArg.vaultId}/${queryArg.action}`,
        method: 'PUT',
      }),
    }),
    apiControllerAddVault: build.mutation<
      ApiControllerAddVaultApiResponse,
      ApiControllerAddVaultApiArg
    >({
      query: (queryArg) => ({
        url: `/web/add_vault`,
        method: 'POST',
        body: queryArg.addVaultRequest,
      }),
    }),
    apiControllerUpdateVault: build.mutation<
      ApiControllerUpdateVaultApiResponse,
      ApiControllerUpdateVaultApiArg
    >({
      query: (queryArg) => ({
        url: `/web/update_vault`,
        method: 'PUT',
        body: queryArg.updateVaultRequest,
      }),
    }),
    apiControllerDeleteVault: build.mutation<
      ApiControllerDeleteVaultApiResponse,
      ApiControllerDeleteVaultApiArg
    >({
      query: (queryArg) => ({
        url: `/web/delete_vault/${queryArg.vaultId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerRefreshVaultApiResponse =
  /** status 200 Refreshed vault state */ RefreshVaultResponse;
export type ApiControllerRefreshVaultApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerConvertApiResponse =
  /** status 200 Conversion transaction sent */ ConvertResponse;
export type ApiControllerConvertApiArg = {
  convertRequest: ConvertRequest;
};
export type ApiControllerWithdrawApiResponse =
  /** status 200 Withdrawal transaction sent */ WithdrawResponse;
export type ApiControllerWithdrawApiArg = {
  withdrawRequest: WithdrawRequest;
};
export type ApiControllerGetExecutorsApiResponse =
  /** status 200 Returns executors list for the vault */ ExecutorsResponse;
export type ApiControllerGetExecutorsApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerGetVaultStatsApiResponse =
  /** status 200 Returns aggregated vault stats */ VaultStatsResponse;
export type ApiControllerGetVaultStatsApiArg = {
  /** Vault ID */
  vaultId: number;
  /** Start time (unix seconds or ms) */
  from: number;
  /** End time (unix seconds or ms) */
  till: number;
};
export type ApiControllerGetVaultBalanceApiResponse =
  /** status 200 Returns {status, balance} where balance is parsed JSON from Vault.balance */ VaultBalanceResponse;
export type ApiControllerGetVaultBalanceApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerConvertToRawApiResponse =
  /** status 200 Returns converted raw amount as string */ ToRawResponse;
export type ApiControllerConvertToRawApiArg = {
  /** Human readable amount */
  amount: string;
  /** Number of decimals */
  decimals: number;
};
export type ApiControllerGetVaultsApiResponse = /** status 200 Returns vaults */ VaultsResponse;
export type ApiControllerGetVaultsApiArg = {
  /** project ID */
  projectId: number;
  /** Show all vaults (1) or only active */
  all: number;
  /** Show only my vaults */
  mine: number;
  /** Items per page */
  limit: number;
  /** Page number */
  page: number;
  /** Search by token symbols (base/quote) */
  search?: string;
};
export type ApiControllerGetVaultApiResponse =
  /** status 200 Returns vault details */ VaultResponse;
export type ApiControllerGetVaultApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerGetVaultSourceApiResponse =
  /** status 200 Returns the Solidity source code persisted at deploy time */ VaultSourceResponse;
export type ApiControllerGetVaultSourceApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type ApiControllerUpdateVaultStatusApiResponse =
  /** status 200 Vault status updated */ UpdateStatusVaultResponse;
export type ApiControllerUpdateVaultStatusApiArg = {
  /** Vault ID */
  vaultId: number;
  /** Action: enable|disable (aliases: start|stop) */
  action: string;
};
export type ApiControllerAddVaultApiResponse = /** status 200 Vault created */ AddVaultResponse;
export type ApiControllerAddVaultApiArg = {
  addVaultRequest: AddVaultRequest;
};
export type ApiControllerUpdateVaultApiResponse =
  /** status 200 Vault updated */ UpdateVaultResponse;
export type ApiControllerUpdateVaultApiArg = {
  updateVaultRequest: UpdateVaultRequest;
};
export type ApiControllerDeleteVaultApiResponse =
  /** status 200 Vault soft-deleted */ DeleteVaultResponse;
export type ApiControllerDeleteVaultApiArg = {
  /** Vault ID */
  vaultId: number;
};
export type RefreshVaultExecutorItem = {
  /** Executor ID */
  id: number;
  /** Executor on-chain address */
  address: string;
  /** Native balance (human readable) */
  balance: number;
};
export type RefreshVaultData = {
  /** Vault ID */
  id: number;
  /** Parsed balance JSON: native and tokens, human-readable */
  balance: {
    [key: string]: any;
  };
  /** Vault price in quote token */
  priceInQuote: number;
  /** Vault price in USD */
  priceInUsd: number;
  /** Executors of the vault with refreshed balances (empty for non-contract vaults) */
  executors: RefreshVaultExecutorItem[];
};
export type RefreshVaultResponse = {
  /** Operation status */
  status: boolean;
  data: RefreshVaultData;
};
export type ConvertData = {
  /** Operation type executed */
  type?: 'wrap' | 'unwrap' | 'legacy';
  /** Transaction hash (if available) */
  txHash?: string;
  /** Gas used by the transaction */
  gasUsed?: number;
  /** Gas spent in native currency */
  gasSpent?: number;
  /** Native currency symbol used to pay gas */
  gasCurrency?: string;
  /** Gas spent in USD */
  gasSpentUSD?: number;
  /** Sender address (vault wallet) */
  from?: string;
  /** Destination contract address (wrapped token) */
  to?: string;
  /** ISO timestamp of the operation */
  timestamp?: string;
};
export type ConvertResponse = {
  /** Operation status */
  status: boolean;
  data: ConvertData;
};
export type ConvertRequest = {
  /** Vault ID (must be a noncustodial / contract vault) */
  pid: number;
  /** Operation type:
    - `wrap` — wraps full native balance via `walletContract.wrap()`
    - `unwrap` — unwraps full wrapped balance via `walletContract.unwrap()`
    - `legacy` — wraps full native balance via legacy `walletContract.convert()` (old templates) */
  type: 'wrap' | 'unwrap' | 'legacy';
};
export type WithdrawData = {
  /** Transaction hash */
  txHash?: string;
  /** Withdrawn amount (human readable) */
  amount?: number;
  /** Withdrawn raw amount (string) */
  rawAmount?: string;
  /** Token symbol */
  token?: string;
  /** Gas used by the transaction */
  gasUsed?: number;
  /** Gas spent in native currency */
  gasSpent?: number;
  /** Native currency symbol used to pay gas */
  gasCurrency?: string;
  /** Gas spent in USD */
  gasSpentUSD?: number;
  /** Source address (vault wallet) */
  from?: string;
  /** Destination address (configured withdrawer) */
  to?: string;
  /** ISO timestamp of the operation */
  timestamp?: string;
};
export type WithdrawResponse = {
  /** Operation status */
  status: boolean;
  data: WithdrawData;
};
export type WithdrawRequest = {
  /** Vault ID */
  pid: number;
  /** Token type to withdraw */
  token: 'base' | 'quote' | 'native' | 'address';
  /** Amount to withdraw (0 = withdraw all available) */
  amount?: number;
  /** Token contract address (required only when token=address) */
  address?: string;
};
export type ExecutorItem = {
  id: number;
  address: string;
  type?: number;
  txCount?: number;
  balance?: number;
};
export type ExecutorsData = {
  executors: ExecutorItem[];
};
export type ExecutorsResponse = {
  /** Operation status */
  status: boolean;
  data: ExecutorsData;
};
export type VaultStatsBucket = {
  /** 0 = sell base, 1 = buy base */
  tx_direction: 0 | 1;
  /** Sum of input token amounts (human readable) */
  volumeIn: number;
  /** Sum of output token amounts (human readable) */
  volumeOut: number;
  /** Sum of trade volume converted to USD */
  volumeUSD: number;
  /** Sum of gas spent in native currency */
  gasSpent: number;
  /** Number of successful transactions in the bucket */
  count: number;
  /** Average trade price in quote token */
  avgPrice: number;
  /** Average trade price in USD */
  avgPriceUSD: number;
  /** Average gas spent per transaction in native currency */
  cost: number;
};
export type VaultStatsResponse = {
  /** Operation status */
  status: boolean;
  /** Aggregated stats by direction (index 0 = sell base, 1 = buy base). Always returns exactly two items. */
  data: VaultStatsBucket[];
};
export type VaultBalanceData = {
  /** Parsed balance JSON keyed by token symbol */
  balance: {
    [key: string]: any;
  };
};
export type VaultBalanceResponse = {
  /** Operation status */
  status: boolean;
  data: VaultBalanceData;
};
export type ToRawData = {
  /** Raw amount as string */
  amount: string;
};
export type ToRawResponse = {
  /** Operation status */
  status: boolean;
  data: ToRawData;
};
export type VaultListItem = {
  id: number;
  type: number;
  label?: string;
  gasLimit?: number;
  baseSymbol?: string;
  baseName?: string;
  quoteSymbol?: string;
  nativeName?: string;
  sellers?: number;
  buyers?: number;
  /** Buy fee fraction (0..1) */
  buyFee?: number;
  /** Sell fee fraction (0..1) */
  sellFee?: number;
  /** Pool fee fraction (0..1) */
  poolFee?: number;
  priceInQuote?: number;
  priceInUsd?: number;
  active: number;
  /** Vault creation date */
  created?: string | null;
  address?: string | null;
  /** Parsed balance JSON keyed by token symbol (native and tokens, human-readable). Null when balance has not been refreshed yet. */
  balance: {
    [key: string]: any;
  } | null;
  authorCrc?: number;
  authorName?: string;
  tasksCountInactive?: number;
  tasksCountActive?: number;
  blockchainName?: string;
  blockchainTestnet?: number;
  exchangeName?: string;
  exchangeProtocol?: string;
  exchangePoolDeployer?: string;
};
export type VaultsData = {
  /** Total items count */
  total: number;
  data: VaultListItem[];
};
export type VaultsResponse = {
  /** Operation status */
  status: boolean;
  data: VaultsData;
};
export type VaultBlockchainInfo = {
  id: number;
  name: string;
  /** Blockchain type (0 = EVM, ...) */
  type: number;
  chainId?: number;
  scanUrl?: string | null;
  rpc?: string | null;
  nativeTokenName?: string | null;
  nativeUSDRate?: number | null;
  testnet?: boolean;
  swapGasLimit?: number | null;
  deployGasLimit?: number | null;
  approveGasLimit?: number | null;
  transferGasLimit?: number | null;
  swapMaxGasUSD?: number | null;
  deployerLevelAlert?: number | null;
  executorLowLimit?: number | null;
  executorTopupMax?: number | null;
  averageSwapGasUsed?: number | null;
};
export type VaultExchangeInfo = {
  name: string;
  protocol?: string | null;
  poolDeployer?: string | null;
  /** 1 = pair quote token is auto-wrapped/unwrapped to native; 0 = regular ERC20 */
  tradeNative?: number | null;
};
export type VaultData = {
  id: number;
  /** Vault type (1 = contract, 2 = custodial) */
  type: number;
  pjId?: number | null;
  label?: string | null;
  baseSymbol?: string | null;
  baseName?: string | null;
  quoteSymbol?: string | null;
  /** 1 = active, 0 = inactive */
  active: number;
  sellers?: number | null;
  buyers?: number | null;
  address?: string | null;
  /** Parsed balance JSON keyed by token symbol (native and tokens, human-readable). Null when balance has not been refreshed yet. */
  balance: {
    [key: string]: any;
  } | null;
  /** Buy fee fraction (0..1) */
  buyFee: number;
  /** Sell fee fraction (0..1) */
  sellFee: number;
  /** Pool fee fraction (0..1) */
  poolFee: number;
  quoteToken?: string | null;
  baseToken?: string | null;
  quoteUnits?: string | null;
  baseUnits?: string | null;
  baseDecimals?: number | null;
  quoteDecimals?: number | null;
  /** Native token symbol (e.g. ETH) */
  nativeTokenName?: string | null;
  /** Wrapped native token address (e.g. WETH) */
  nativeTokenAddress?: string | null;
  /** Price in quote token */
  priceInQuote: number;
  /** Price in USD */
  priceInUsd: number;
  withdrawer?: string | null;
  receiver?: string | null;
  poolAddress?: string | null;
  /** CRC32 of author UID */
  authorCrc: number;
  authorName?: string | null;
  /** Creation date (ISO) */
  created: string;
  /** Pool-specific parameters resolved at vault creation (V4: fee, tickSpacing, hooks, parameters, etc.). Parsed JSON object. */
  details?: {
    [key: string]: any;
  } | null;
  blockchain?: VaultBlockchainInfo | null;
  exchange?: VaultExchangeInfo | null;
  /** Assigned deployer ID */
  deployerId?: number | null;
};
export type VaultResponse = {
  /** Operation status */
  status: boolean;
  data: VaultData;
};
export type VaultSourceData = {
  /** Vault ID */
  vaultId: number;
  /** Full Solidity source code persisted on deploy */
  source: string;
};
export type VaultSourceResponse = {
  /** Operation status */
  status: boolean;
  data: VaultSourceData;
};
export type UpdateStatusVaultData = {
  /** Vault ID */
  vaultId: number;
  /** Requested action */
  action: 'enable' | 'disable' | 'start' | 'stop';
  /** Resulting active flag (0 or 1) */
  active: number;
};
export type UpdateStatusVaultResponse = {
  /** Operation status */
  status: boolean;
  data: UpdateStatusVaultData;
};
export type AddVaultData = {
  /** Created vault ID */
  id: number;
  /** On-chain address (if deployed) */
  address?: string | null;
  /** Result message */
  message: string;
};
export type AddVaultResponse = {
  /** Operation status */
  status: boolean;
  data: AddVaultData;
};
export type AddVaultRequest = {
  label?: string;
  /** Vault type: 1 = contract (non-custodial), 2 = custodial wallet, 3 = CL management (concentrated liquidity, non-custodial). For type=3 the chosen exchange must have cl=1, clContractTemplate and nftManager configured. */
  vaultType: number;
  /** Blockchain ID */
  blockchainId: number;
  /** Exchange ID */
  exchangeId: number;
  /** Base token address */
  base: string;
  /** Quote token address */
  quote: string;
  withdrawer: string;
  receiver?: string | number;
  pool?: string | null;
  /** 0..1 */
  poolFee?: number;
  /** Buy fee fraction (0..1) */
  buyFee?: number;
  /** Sell fee fraction (0..1) */
  sellFee?: number;
  sellers?: number;
  buyers?: number;
  telegram?: number;
  /** Deployer ID (EVM only) */
  did?: number;
  /** Optional wallet private key */
  private?: string;
  /** If true, try to deploy vault right after creation (non-custodial only) */
  deployNow?: boolean;
  /** Arbitrary vault details object (e.g. { validator: "5..." } for Bittensor). Stored as JSON string. */
  details?: {
    [key: string]: any;
  };
};
export type UpdateVaultData = {
  /** Vault ID */
  id: number;
  /** Result message */
  message: string;
};
export type UpdateVaultResponse = {
  /** Operation status */
  status: boolean;
  data: UpdateVaultData;
};
export type UpdateVaultRequest = {
  /** Vault ID */
  pid: number;
  label?: string;
  /** Buy fee fraction (0..1) */
  buyFee?: number;
  /** Sell fee fraction (0..1) */
  sellFee?: number;
  /** 0..1 */
  poolFee?: number;
  sellers?: number;
  buyers?: number;
  gasLimit?: number;
  withdrawer?: string;
  receiver?: string | number;
  poolAddress?: string | null;
  /** Base token address (optional) */
  base?: string;
  /** Quote token address (optional) */
  quote?: string;
};
export type DeleteVaultData = {
  /** Vault ID that was soft-deleted */
  id: number;
};
export type DeleteVaultResponse = {
  /** Operation status */
  status: boolean;
  data: DeleteVaultData;
};
